import React, { memo, useMemo, useCallback, useState } from 'react'
import ApolloErrorMessages from 'transfix-ui/components/ApolloErrorMessages'
import {
  ConfirmationModal,
  IConfirmationModalProps,
} from 'transfix-ui/components/Modals/ConfirmationModal'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { IError, IBidApprovalStateEnum } from 'types/graphqlTypes'
import { useMPAnalytics } from 'apps/marketplace/hooks'
import { TextArea, ITextAreaProps } from 'transfix-ui'
import { TEST_IDS } from './constants'
import { useRejectOptionMutation } from './graphql/RejectOptionMutation'

export interface IRejectOptionConfirmationModalProps
  extends Pick<IConfirmationModalProps, 'onClose'> {
  bidApprovalId: string
  onSuccess: () => void
}

const RejectOptionConfirmationModal = memo<IRejectOptionConfirmationModalProps>(
  ({ bidApprovalId, onClose, onSuccess }) => {
    const { trackEvent } = useMPAnalytics()
    const { openToast } = useGlobalToast()
    const [notes, setNotes] = useState<ITextAreaProps['value']>('')
    const [mutationErrors, setMutationErrors] = useState<IError[]>([])

    const notesChangeHandler: ITextAreaProps['onChange'] = e => {
      setNotes(e.target.value)
    }

    const [rejectOptionMutation, { error, loading }] = useRejectOptionMutation()

    const rejectHandler = useCallback(async () => {
      setMutationErrors([])
      const { data } = await rejectOptionMutation({
        variables: {
          input: {
            id: bidApprovalId,
            state: IBidApprovalStateEnum.declined,
            comment: notes,
          },
        },
      })

      const { updateBidApprovalState } = data || {}
      const { bidApproval, errors = [] } = updateBidApprovalState || {}
      const { id: bidId } = bidApproval || {}

      if (errors.length) {
        trackEvent('Carrier Options', 'ERROR_REJECTING_OPTION_APPROVAL', { bidApprovalId, errors })
        setMutationErrors(errors)
      }

      if (bidId) {
        trackEvent('Carrier Options', 'REJECTED_OPTION_APPROVAL', { bidApprovalId, bidId })
        openToast({ toastMessage: 'Option rejected', toastType: 'default' })
        onSuccess()
      }
    }, [rejectOptionMutation, bidApprovalId, notes, trackEvent, openToast, onSuccess])

    const actions: IConfirmationModalProps['actions'] = useMemo(
      () => [
        {
          label: 'Cancel',
          disabled: loading,
          type: 'dismiss',
          action: onClose,
        },
        {
          label: 'Reject',
          disabled: loading,
          type: 'confirm',
          dataTest: TEST_IDS.submitButton,
          action: rejectHandler,
        },
      ],
      [onClose, loading, rejectHandler]
    )
    return (
      <ConfirmationModal
        title='Reject this option?'
        dataTest={TEST_IDS.modal}
        actions={actions}
        onClose={onClose}
        open>
        <ApolloErrorMessages mutationErrors={mutationErrors} apolloErrors={error} />
        <TextArea
          size='medium'
          label='Note (Optional)'
          placeholder='Add notes'
          onChange={notesChangeHandler}
          value={notes}
        />
      </ConfirmationModal>
    )
  }
)

export default RejectOptionConfirmationModal
